import React, { useContext, useEffect, useRef, useState } from 'react';
import { addDays } from 'date-fns';
import _isEmpty from 'lodash/isEmpty';
import _throttle from 'lodash/throttle';
import PropTypes from 'prop-types';
import InfiniteScroll from 'react-infinite-scroller';
import { defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';

import Button from 'components/Button';
import LoadingDots from 'components/LoadingDots';
import Message from 'components/Message';
import When from 'components/When';
import { PERMISSION } from 'constants/logic';
import { filterSchedulesByScheduleFilterKey } from 'features/ScheduleFilters';
import { bem } from 'helpers/class-name';
import { userHasPermission } from 'helpers/core';
import { useIsScheduleFiltersV2Enabled } from 'hooks/useIsScheduleFiltersV2Enabled';

import VenueSchedulesContext from '../Contexts/VenueSchedulesContext';
import EmptyDetail from '../EmptyDetail';
import {
  DEFAULT_SCHEDULE_LIST_FILTER,
  SCHEDULE_LIST_FILTER_OPTIONS_BY_KEY,
  filterSchedules,
} from '../ScheduleList.helpers';
import { selectScheduleListFilter } from '../YieldManagement.selectors';
import ScheduleListItem from './ScheduleListItemContainer';

const SCROLL_THROTTLE_LENGTH = 16 * 2;
const SELECTED_ITEM_BOX_SHADOW_SIZE = 15;
const HEADER_HEIGHT = 48;

const messages = {
  ...defineMessages({
    noClasses: {
      id: 'yield-management.schedule-list.no-classes-on-date',
      defaultMessage: 'You have no classes on your schedule for {startDate, date, long-no-year}.',
    },
    tryTomorrow: {
      id: 'yield-management.schedule-list.try-tomorrow',
      defaultMessage: 'Try the next day',
    },
  }),
  header: defineMessages({
    allBookings: {
      id: 'yield-management.schedule-list.all-bookings',
      defaultMessage: 'All Bookings',
    },
    cpReservations: {
      id: 'yield-management.schedule-list.cp-reservations',
      defaultMessage: 'CP',
    },
  }),
};

const { element } = bem('schedule-list');

const ScheduleList = ({
  isDesktop,
  isUserLoaded,
  ListItemComponent = ScheduleListItem,
  permissions,
  setSelectedSchedule,
  showDirectFillData,
  showListHeader = true,
  startDate,
  updateStartDate,
  venueId,
  venueTimezone,
}) => {
  const {
    activeSchedule: selectedSchedule,
    isLoading: isLoadingSchedules,
    isLoadingMore: isLoadingMoreSchedules,
    loadMoreVenueSchedules,
    pagination,
    schedules: allSchedules = [],
  } = useContext(VenueSchedulesContext);
  const { page: schedulesCurrentPage, hasMore: hasMoreSchedules } = pagination;
  const [schedules, setSchedules] = useState([]);

  const schedulesFilterKey = useSelector(selectScheduleListFilter);
  const isScheduleFiltersV2Enabled = useIsScheduleFiltersV2Enabled(venueId);
  const hasSpotManagePermission = userHasPermission({ permissions }, PERMISSION.DISPLAY_SPOT_MANAGEMENT);

  useEffect(() => {
    if (isLoadingSchedules) {
      setSchedules([]);
      return;
    }

    if (isScheduleFiltersV2Enabled === true) {
      setSchedules(filterSchedulesByScheduleFilterKey(allSchedules, schedulesFilterKey, { hasSpotManagePermission }));
      return;
    }

    const legacyFilterKey = SCHEDULE_LIST_FILTER_OPTIONS_BY_KEY[schedulesFilterKey]
      ? schedulesFilterKey
      : DEFAULT_SCHEDULE_LIST_FILTER;
    setSchedules(filterSchedules(allSchedules, legacyFilterKey));
  }, [
    hasSpotManagePermission,
    isLoadingSchedules,
    isScheduleFiltersV2Enabled,
    JSON.stringify(allSchedules),
    schedulesFilterKey,
  ]);

  const handleLoadMoreSchedules = () => {
    if (!isLoadingMoreSchedules && hasMoreSchedules) {
      loadMoreVenueSchedules({ date: startDate, page: schedulesCurrentPage + 1, venueId });
    }
  };

  useEffect(() => {
    // Load more schedules if the list is empty after filter is applied
    if (!isLoadingSchedules && schedules.length === 0) {
      handleLoadMoreSchedules();
    }
  }, [isLoadingSchedules, isLoadingMoreSchedules, JSON.stringify(schedules)]);

  const isLoaded = isUserLoaded && !isLoadingSchedules && !(schedules.length === 0 && isLoadingMoreSchedules);

  const prevSelectedScheduleRef = useRef({});
  const registeredNodes = useRef({});
  const scrollContainer = useRef(null);
  const [visibleScheduleIds, setVisibleScheduleIds] = useState([]);

  const handleSetNextDay = () => {
    const nextDay = addDays(startDate, 1);
    updateStartDate(nextDay);
  };

  const registerNode = (scheduleId, isInThePast, node) => {
    registeredNodes.current = {
      ...registeredNodes.current,
      [scheduleId]: { node, isInThePast, height: node.offsetHeight, top: node.offsetTop },
    };
  };

  const handleListScroll = _throttle(() => {
    if (scrollContainer.current) {
      const scrollContainerScrollTop = scrollContainer.current.scrollTop;
      const scrollContainerHeight = scrollContainer.current.offsetHeight;

      setVisibleScheduleIds(
        Object.keys(registeredNodes.current).filter(scheduleId => {
          const node = registeredNodes.current[scheduleId];
          return (
            scrollContainerScrollTop <= node?.top - HEADER_HEIGHT - SELECTED_ITEM_BOX_SHADOW_SIZE * 2 &&
            scrollContainerScrollTop + scrollContainerHeight >= node?.top + node?.height
          );
        })
      );
    }
  }, SCROLL_THROTTLE_LENGTH);

  const selectDefaultSchedule = () => {
    const nextSchedule = schedules.find(({ scheduleId }) => !(registeredNodes.current[scheduleId] || {}).isInThePast);
    const { scheduleId } = nextSchedule || schedules[0] || {};

    if (scheduleId) {
      setSelectedSchedule({
        venueId,
        scheduleId,
      });
    }
  };

  const scrollToSelectedSchedule = () => {
    const { scheduleId } = selectedSchedule;
    const listItem = registeredNodes.current[scheduleId] || {};

    if (scheduleId && listItem.node) {
      if (isDesktop && scrollContainer.current) {
        scrollContainer.current.scrollTop = listItem.top - HEADER_HEIGHT - SELECTED_ITEM_BOX_SHADOW_SIZE * 2;
      } else {
        window.scrollTo(0, listItem.top);
      }
    }
  };

  useEffect(() => {
    const prevSelectedSchedule = prevSelectedScheduleRef.current;
    if (isLoaded) {
      if (_isEmpty(selectedSchedule)) {
        selectDefaultSchedule();
      } else if (_isEmpty(prevSelectedSchedule)) {
        scrollToSelectedSchedule();
      }
    }

    if (scrollContainer.current) {
      scrollContainer.current.addEventListener('scroll', handleListScroll);
    }
    window.addEventListener('resize', handleListScroll);

    return () => {
      if (scrollContainer.current) {
        scrollContainer.current.removeEventListener('scroll', handleListScroll);
      }
      window.removeEventListener('resize', handleListScroll);
    };
  }, [isLoaded, schedules, selectedSchedule]);

  useEffect(() => {
    prevSelectedScheduleRef.current = selectedSchedule;
  }, [selectedSchedule]);

  return (
    <>
      <When predicate={!isLoaded}>
        <EmptyDetail isLoading />
      </When>
      <When predicate={isLoaded}>
        <div ref={scrollContainer} className={element('scroll-container')}>
          <When predicate={showListHeader}>
            <div className={element('header')}>
              <When predicate={showDirectFillData}>
                <Message
                  as="div"
                  className={element('header-column', { all: true })}
                  message={messages.header.allBookings}
                />
              </When>
              <Message
                as="div"
                className={element('header-column', { cp: true })}
                message={messages.header.cpReservations}
              />
            </div>
          </When>
          <When predicate={_isEmpty(schedules)}>
            <div className={element('empty')}>
              <Message
                as="p"
                className={element('empty-text')}
                message={messages.noClasses}
                values={{
                  startDate,
                }}
              />
              <Message
                as={Button}
                onClick={handleSetNextDay}
                className={element('empty-btn')}
                styled="primary"
                message={messages.tryTomorrow}
              />
            </div>
          </When>
          <When predicate={!_isEmpty(schedules)}>
            <InfiniteScroll
              initialLoad={false}
              loadMore={handleLoadMoreSchedules}
              hasMore={hasMoreSchedules}
              getScrollParent={() => scrollContainer.ref}
              loader={
                <div key="schedule-loading-dots" className={element('loading-wrapper')}>
                  <LoadingDots />
                </div>
              }
              threshold={1000}
              useWindow={!isDesktop}>
              {schedules.map((schedule, i) => {
                const { scheduleId } = schedule;
                const { scheduleId: selectedScheduleId } = selectedSchedule;
                return (
                  <ListItemComponent
                    key={scheduleId || i}
                    isLoadingMoreSchedules={isLoadingMoreSchedules}
                    hasMoreSchedules={hasMoreSchedules}
                    loadMoreSchedules={handleLoadMoreSchedules}
                    schedule={schedule}
                    isSelected={scheduleId === selectedScheduleId}
                    setSelectedSchedule={setSelectedSchedule}
                    permissions={permissions}
                    venueId={venueId}
                    venueTimezone={venueTimezone}
                    registerNode={registerNode}
                    visible={visibleScheduleIds.includes(scheduleId.toString())}
                    isLastItem={i === schedules.length - 1}
                    isDesktop={isDesktop}
                  />
                );
              })}
            </InfiniteScroll>
          </When>
        </div>
      </When>
    </>
  );
};

ScheduleList.propTypes = {
  isDesktop: PropTypes.bool,
  isUserLoaded: PropTypes.bool,
  ListItemComponent: PropTypes.elementType,
  setSelectedSchedule: PropTypes.func,
  showListHeader: PropTypes.bool,
  startDate: PropTypes.instanceOf(Date),
  updateStartDate: PropTypes.func,
  permissions: PropTypes.arrayOf(PropTypes.string),
  venueId: PropTypes.number,
  venueTimezone: PropTypes.string,
  showDirectFillData: PropTypes.bool,
};

export default ScheduleList;
